// src/features/commodities-ranking/api.ts
//
// Data fetcher for the Commodities ranking page. Sibling of
// features/etf-ranking/api.ts. Talks to the FastAPI commodities-ranking
// router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>)
//     so the auth-gated /api/v1/commodities-ranking router is reached as
//     an authorized user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/commodities-ranking/...` path here —
// adding `/api/v1` would double the prefix. See api/client.ts
// (REQUIRED_API_PREFIX).
//
// Unlike the ETF domain, the commodities scorer emits the STOCK output
// schema (sector / momentum / fundamental / sentiment / forge_ultra_signal
// / sector_rank), so the row shape below mirrors the equity contract in
// app/core/cache_keys.py (_TOP_SIGNALS_ROW_KEYS). The three factor columns
// map onto the commodities signal logic:
//   - momentum_z_score    -> price momentum (126-day cumulative return)
//   - fundamental_z_score -> Net Commercial COT positioning (hedgers)
//   - sentiment_z_score   -> Net Non-Commercial COT positioning (specs)
// There is NO quick-stats endpoint; the bespoke `cot/{symbol}` endpoint
// (see fetchCotHistory) surfaces the weekly net-positioning history that
// distinguishes this domain.

import { apiClient } from '../../api/client';

// Matches the exact JSON row shape from GET
// /api/v1/commodities-ranking/top-assets and /universe. Byte-identical to
// the equity RankingRow contract; keep them in lock-step.
export interface CommoditiesRankingRow {
    symbol: string;
    sector: string;
    forge_ultra_signal: number;
    annualized_volatility: number;
    momentum_z_score: number;
    fundamental_z_score: number;
    sentiment_z_score: number;
    universe_rank: number;
    sector_rank: number;
}

export interface TopAssetsResponse {
    as_of_date: string;
    top_n: number;
    rankings: CommoditiesRankingRow[];
}

export const fetchTopAssets = async (): Promise<TopAssetsResponse> => {
    // Resolves to GET /api/v1/commodities-ranking/top-assets.
    const { data } = await apiClient.get<TopAssetsResponse>(
        '/commodities-ranking/top-assets',
    );
    return data;
};

// --- Full universe (curated ~23 commodities, non-paginated) -------------
// The backend returns the entire cross-section for the latest date in one
// payload (optionally narrowed server-side via ?category=). Each universe
// row additionally carries the curated human-readable instrument `name`.

export interface UniverseRow extends CommoditiesRankingRow {
    name: string;
}

export interface UniverseResponse {
    as_of_date: string;
    total: number;
    items: UniverseRow[];
}

export const fetchUniverse = async (
    category?: string,
): Promise<UniverseResponse> => {
    // Resolves to GET /api/v1/commodities-ranking/universe.
    const { data } = await apiClient.get<UniverseResponse>(
        '/commodities-ranking/universe',
        { params: category ? { category } : undefined },
    );
    return data;
};

// --- Batched historical trajectories (sparklines) ------------------------
// One request for many symbols collapses what would be an N+1 fetch
// waterfall (one call per visible table row) into a single round-trip.

export interface HistoryPoint {
    date: string;
    forge_ultra_signal: number;
}

// Map of {symbol: [{date, forge_ultra_signal}, ...]}. Every requested
// symbol is present as a key (empty array if it has no rows in window).
export type HistoricalBatchResponse = Record<string, HistoryPoint[]>;

export const fetchHistoricalBatch = async (
    symbols: string[],
    days = 90,
): Promise<HistoricalBatchResponse> => {
    if (symbols.length === 0) return {};
    const { data } = await apiClient.get<HistoricalBatchResponse>(
        '/commodities-ranking/historical-batch',
        { params: { symbols: symbols.join(','), days } },
    );
    return data;
};

// --- Category summary (radar / heat strip) -------------------------------
// Per-category average factor z-scores, aggregated server-side over the
// latest date so the client receives a handful of rows (Energy / Metals /
// Agriculture / Livestock) instead of the full cross-section.

export interface CategorySummaryRow {
    category: string;
    count: number;
    avg_momentum_z: number;
    avg_fundamental_z: number;
    avg_sentiment_z: number;
    avg_signal: number;
}

export interface CategorySummaryResponse {
    as_of_date: string;
    categories: CategorySummaryRow[];
}

export const fetchCategorySummary =
    async (): Promise<CategorySummaryResponse> => {
        // Resolves to GET /api/v1/commodities-ranking/category-summary.
        const { data } = await apiClient.get<CategorySummaryResponse>(
            '/commodities-ranking/category-summary',
        );
        return data;
    };

// --- COT net-positioning history (symbol pop-over) -----------------------
// Weekly Commercial (hedger) vs Non-Commercial (speculator) net positions
// for a single commodity. Powers the click-to-open positioning pop-over on
// the universe table — the commodities analogue of the equity/ETF
// quick-stats modal. `symbol` is the FMP pricing ticker (e.g. GCUSD); the
// backend maps it to the CFTC ticker internally, so the dashboard uses one
// symbol space. Returns an empty `series` (not a 404) for commodities with
// no COT coverage.

export interface CotPoint {
    date: string;
    net_commercial: number;
    net_noncomm: number;
    commercial_long: number;
    commercial_short: number;
    noncomm_long: number;
    noncomm_short: number;
}

export interface CotHistoryResponse {
    symbol: string;
    cot_symbol: string;
    name: string;
    count: number;
    series: CotPoint[];
}

export const fetchCotHistory = async (
    symbol: string,
    days = 365,
): Promise<CotHistoryResponse> => {
    // Resolves to GET /api/v1/commodities-ranking/cot/<symbol>.
    const { data } = await apiClient.get<CotHistoryResponse>(
        `/commodities-ranking/cot/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};
